import { useEffect, useRef } from "react";
import { Lightbulb, Highlighter, Volume2 } from "lucide-react";
import type { PdfSelection } from "./types";

const ChatIcon = ({ className }: { className?: string }) => (
  <svg
    stroke="currentColor"
    fill="currentColor"
    strokeWidth="0"
    viewBox="0 0 256 256"
    className={className}
    height="1em"
    width="1em"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M128,20A108,108,0,0,0,31.85,177.23L21,209.66A20,20,0,0,0,46.34,235l32.43-10.81A108,108,0,1,0,128,20Zm0,192a84,84,0,0,1-42.06-11.27,12,12,0,0,0-6-1.62,12.1,12.1,0,0,0-3.8.62l-29.79,9.93,9.93-29.79a12,12,0,0,0-1-9.81A84,84,0,1,1,128,212Z" />
  </svg>
);

const AddToNotesIcon = ({ className }: { className?: string }) => (
  <svg
    stroke="currentColor"
    fill="currentColor"
    strokeWidth="0"
    viewBox="0 0 256 256"
    className={className}
    height="1em"
    width="1em"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M229.66,58.34l-32-32a8,8,0,0,0-11.32,0l-96,96A8,8,0,0,0,88,128v32a8,8,0,0,0,8,8h32a8,8,0,0,0,5.66-2.34l96-96A8,8,0,0,0,229.66,58.34ZM124.69,152H104V131.31l64-64L188.69,88ZM200,76.69,179.31,56,192,43.31,212.69,64ZM224,128v80a16,16,0,0,1-16,16H48a16,16,0,0,1-16-16V48A16,16,0,0,1,48,32h80a8,8,0,0,1,0,16H48V208H208V128a8,8,0,0,1,16,0Z" />
  </svg>
);

interface ToolbarAction {
  id: string;
  label: string;
  iconClassName: string;
  renderIcon: (className: string) => React.ReactNode;
}

const toolbarActions: ToolbarAction[] = [
  {
    id: "explain",
    label: "Explain",
    iconClassName: "h-3.5 w-3.5 group-hover/button:text-premium",
    renderIcon: (className) => <Lightbulb className={className} />,
  },
  {
    id: "chat",
    label: "Chat",
    iconClassName: "h-3.5 w-3.5 group-hover/button:text-teal",
    renderIcon: (className) => <ChatIcon className={className} />,
  },
  {
    id: "add-to-notes",
    label: "Add to notes",
    iconClassName: "h-3.5 w-3.5 group-hover/button:text-info",
    renderIcon: (className) => <AddToNotesIcon className={className} />,
  },
  {
    id: "highlight",
    label: "Highlight",
    iconClassName: "h-3.5 w-3.5 group-hover/button:text-warning",
    renderIcon: (className) => <Highlighter className={className} />,
  },
  {
    id: "listen",
    label: "Listen",
    iconClassName: "h-3.5 w-3.5 group-hover/button:text-correct",
    renderIcon: (className) => <Volume2 className={className} />,
  },
];

type Props = {
  selection: PdfSelection;
  onAction: (actionId: string) => void;
  onClose: () => void;
};

export function PdfFloatingMenu({ selection, onAction, onClose }: Props) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const r = selection.anchorClientRect;
    const toolbarWidth = 520;

    // Position below selection
    const top = r.bottom + 8;

    let left = r.left + r.width / 2 - toolbarWidth / 2;
    const padding = 16;
    if (left < padding) left = padding;
    if (left + toolbarWidth > window.innerWidth - padding) {
      left = window.innerWidth - toolbarWidth - padding;
    }

    el.style.top = `${top}px`;
    el.style.left = `${left}px`;
  }, [selection]);

  return (
    <div
      ref={ref}
      className="fixed z-[9999] mt-1.5 flex items-center justify-between rounded-lg border border-border bg-background p-1 shadow-md dark:border-primary/10 dark:bg-card"
      role="toolbar"
      aria-label="PDF selection actions"
      onMouseDown={(e) => e.preventDefault()}
    >
      <div className="flex items-center">
        {toolbarActions.map((action, index) => (
          <div key={action.id} className="flex items-center">
            {index > 0 && (
              <div className="mx-1 h-6 w-px bg-border dark:bg-primary/10" />
            )}
            <button
              className="group/button flex h-9 w-full items-center justify-start gap-1.5 whitespace-nowrap rounded-md px-2 py-0.5 text-sm font-medium ring-offset-background transition-colors hover:bg-primary/5 hover:text-accent-foreground focus-visible:outline-none disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 dark:hover:bg-primary/10"
              aria-label={action.label}
              onClick={() => onAction(action.id)}
            >
              <div className="flex-shrink-0">
                {action.renderIcon(action.iconClassName)}
              </div>
              <span className="text-primary/80 group-hover/button:text-primary dark:text-primary/80 dark:group-hover/button:text-primary/100">
                {action.label}
              </span>
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
